import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useImpersonation } from '@/contexts/ImpersonationContext';

export interface TeamAgent {
  id: string;
  team_id: string;
  user_id: string | null;
  name: string;
  headshot_url: string | null;
  created_at: string;
  updated_at: string;
  is_user_linked: boolean;
  user_email: string | null;
  norths_count: {
    published: number;
    unpublished: number;
    total: number;
  };
}

async function fetchTeamAgents(effectiveUserId: string): Promise<TeamAgent[]> {
  if (!effectiveUserId) throw new Error('Not authenticated');

  // Get user's team
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('team_id')
    .eq('user_id', effectiveUserId)
    .single();

  if (profileError) throw profileError;
  if (!profile?.team_id) {
    return []; // No team, return empty array
  }

  // Fetch team agents
  const { data: agents, error: agentsError } = await supabase
    .from('team_agents')
    .select('id, team_id, user_id, name, headshot_url, created_at, updated_at')
    .eq('team_id', profile.team_id)
    .order('name', { ascending: true });

  if (agentsError) throw agentsError;

  // Get user emails for linked agents
  const userIds = (agents || [])
    .filter(a => a.user_id)
    .map(a => a.user_id) as string[];

  let userEmailsMap = new Map<string, string | null>();
  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('user_id, email')
      .in('user_id', userIds);

    userEmailsMap = new Map(
      (profiles || []).map(p => [p.user_id, p.email])
    );
  }

  // Get North counts for each agent
  // We need to check both custom_agent_name and listing_agent fields
  const agentNames = (agents || []).map(a => a.name);
  
  let northsCountsMap = new Map<string, { published: number; unpublished: number; total: number }>();
  
  if (agentNames.length > 0) {
    // Get all team user IDs to filter Norths by team
    const teamUserIds = (await supabase
      .from('profiles')
      .select('user_id')
      .eq('team_id', profile.team_id)
    ).data?.map(p => p.user_id) || [];

    if (teamUserIds.length > 0) {
      // Query Norths that match any agent name in custom_agent_name or listing_agent
      const { data: norths } = await supabase
        .from('norths')
        .select('custom_agent_name, listing_agent, is_published')
        .in('user_id', teamUserIds)
        .or(`custom_agent_name.in.(${agentNames.map(n => `"${n}"`).join(',')}),listing_agent.in.(${agentNames.map(n => `"${n}"`).join(',')})`);

      // Count Norths per agent name
      agentNames.forEach(agentName => {
        const matchingNorths = (norths || []).filter(n => 
          n.custom_agent_name === agentName || n.listing_agent === agentName
        );
        
        const published = matchingNorths.filter(n => n.is_published).length;
        const unpublished = matchingNorths.filter(n => !n.is_published).length;
        
        northsCountsMap.set(agentName, {
          published,
          unpublished,
          total: published + unpublished,
        });
      });
    }
  }

  // Transform the data to include user info and flags
  return (agents || []).map((agent) => ({
    id: agent.id,
    team_id: agent.team_id,
    user_id: agent.user_id,
    name: agent.name,
    headshot_url: agent.headshot_url,
    created_at: agent.created_at,
    updated_at: agent.updated_at,
    is_user_linked: !!agent.user_id,
    user_email: agent.user_id ? (userEmailsMap.get(agent.user_id) || null) : null,
    norths_count: northsCountsMap.get(agent.name) || { published: 0, unpublished: 0, total: 0 },
  }));
}

export function useTeamAgents() {
  const { getEffectiveUserId } = useImpersonation();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['team-agents'],
    queryFn: async () => {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) throw new Error('Not authenticated');
      return fetchTeamAgents(effectiveUserId);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - cache for faster loading
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: 1,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['team-agents'] });
  };

  return {
    agents: data || [],
    isLoading,
    error,
    refetch,
    invalidate,
  };
}

